import CardWrapper from '../wrapper/card';
import ConfirmationDomainModal from './modal/confirmation';
import ConnectDomainComponent from './connect';
import DNSModal from './modal/dns';
import ImageClass from '../../assets';
import { AppDispatch, RootState } from '../../redux/store';
import { Avatar, Box, Button, CircularProgress, Typography } from '@mui/material';
import { brand, gray } from '../../theme/themePrimitives';
import { ConnectDomainParams, DomainObject } from '../../params/redux/domain';
import { EditNoteOutlined, LaunchRounded } from '@mui/icons-material';
import { fetchDomainDNSThunk, fetchDomainsThunk } from '../../redux/thunks/domain';
import { settingConnectingDomain, settingIndexForConnectingDomain, settingIsConnectingFlagDomain } from '../../redux/slices/domain';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';

const DomainComponent = () => {
    const dispatch = useDispatch<AppDispatch>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showModal, setShowModal] = useState<boolean>(false);
    const [showDNSModal, setShowDNSModal] = useState<boolean>(false);
    const [websiteDetails, setWebsiteDetails] = useState<DomainObject | null>(null);
    const { flags, domain, loaders } = useSelector((state: RootState) => state.domain);

    const flagChangeHandler = () => {
        const data: ConnectDomainParams = {
            domain_name: '',
            domain_seller: '',
            domain_host: '',
            site_title: '',
            website_description: '',
            favicon_image: '',
            social_image: '',
            domain_records: ''
        };
        dispatch(settingConnectingDomain(data));
        dispatch(settingIsConnectingFlagDomain(true));
        dispatch(settingIndexForConnectingDomain(0));
    };

    useEffect(() => {
        const fetchDomain = async () => {
            try {
                setIsLoading(true);
                await dispatch(fetchDomainsThunk()).unwrap();
            } catch (e) {
                //
            } finally {
                setIsLoading(false);
            }
        };
        fetchDomain();
    }, []);

    const editHandler = () => {
        const data: ConnectDomainParams = {
            domain_name: domain?.domain_name || '',
            domain_seller: domain?.domain_seller || '',
            domain_host: domain?.domain_host_1 || '',
            site_title: domain?.site_title || '',
            website_description: domain?.website_description || '',
            favicon_image: domain?.favicon_image || '',
            social_image: domain?.social_image || '',
            domain_records: domain?.domain_record_3 || ''
        };
        dispatch(settingConnectingDomain(data));
        dispatch(settingIsConnectingFlagDomain(true));
        dispatch(settingIndexForConnectingDomain(2));
    };

    const getDNSDetails = async () => {
        try {
            const response = await dispatch(fetchDomainDNSThunk()).unwrap();
            if (response) {
                setShowDNSModal(true);
                setWebsiteDetails(response as DomainObject);
            }
        } catch (e) {
            //
        }
    };

    if (isLoading || loaders.isLoading) {
        return <Box minHeight="60vh" display="flex" flexDirection="column" gap={2} alignItems="center" justifyContent="center">
            <CircularProgress />
            <Typography variant="subtitle1">Loading...</Typography>
        </Box>;
    } else if (flags.isConnecting) {
        return <ConnectDomainComponent />;
    } else if (!domain && !isLoading && !loaders.isLoading) {
        return <Box mx="auto" p={2}>
            <CardWrapper minWidth="75vh" maxWidth="75vh">
                <Box display="flex" justifyContent="center" my={2}>
                    <img
                        src={ImageClass.domain}
                        alt="Domain Image"
                        height="400px"
                    />
                </Box>
                <Typography variant="subtitle1" fontWeight="bold" color="textSecondary" align="center">Why you should connect your domain?</Typography>
                <ul>
                    <li><Typography variant="subtitle2" color="textDisabled">A custom domain name makes your website look more professional and credible.</Typography></li>
                    <li><Typography variant="subtitle2" color="textDisabled">It also helps people to remember your website address and find it more easily.</Typography></li>
                    <li><Typography variant="subtitle2" color="textDisabled">Connecting your domain  can help your website rank higher in search engine results pages (SERPs)</Typography></li>
                    <li><Typography variant="subtitle2" color="textDisabled">Connecting your domain website gives you more control over your website&apos;s appearance and functionality.</Typography></li>
                </ul>
                <Button variant="contained" fullWidth onClick={flagChangeHandler}>Connect Domain</Button>
            </CardWrapper>
        </Box>;
    }

    return (
        <Box p={2}>
            <CardWrapper>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h4">Domain</Typography>
                    <Box display="flex" gap={2} alignItems="center">
                        <Button variant="contained" onClick={getDNSDetails}>Get DNS Details</Button>
                        <Button variant="outlined" startIcon={<EditNoteOutlined />} onClick={editHandler}>Edit Site Settings</Button>
                        <Button variant="outlined" color="error" onClick={() => setShowModal(true)}>Disconnect Domain</Button>
                    </Box>
                </Box>
                <Box mt={3} display="flex" flexDirection="column" gap={1}>
                    <Box display="flex" gap={2} alignItems="center">
                        <Box>
                            <Avatar
                                alt="favicon"
                                src={domain && domain.favicon_image ? `${process.env.REACT_APP_FILE_PATH}${domain?.favicon_image}` : ''}
                                sx={{ bgcolor: gray[200], border: `1px solid ${gray[500]}`, objectFit: 'contain' }}
                            />
                        </Box>
                        <Typography variant="h5">{domain?.domain_name}</Typography>
                    </Box>
                    <Box display="flex" gap={1}><Typography variant="subtitle1" color="textSecondary">Domain:</Typography>
                        <Box display="flex" gap={1} alignItems="center" color={brand[300]}>
                            <a
                                href={domain?.domain_name.includes('https') ? domain?.domain_name : `https://${domain?.domain_name}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    display: 'inline',
                                    color: brand[300]
                                }}
                            >
                                {domain?.domain_name}
                            </a>
                            <LaunchRounded fontSize="small" />
                        </Box>
                    </Box>
                    <Typography variant="subtitle1" color="textSecondary">Managed by third party: {' '}
                        <Typography component="span" variant="subtitle2" color="textDisabled">Connected by DNS</Typography>
                    </Typography>
                </Box>
            </CardWrapper>
            <ConfirmationDomainModal open={showModal} onClose={() => setShowModal(false)} />
            <DNSModal open={showDNSModal} onClose={() => {
                setShowDNSModal(false);
                setWebsiteDetails(null);
            }} websiteDetails={websiteDetails as DomainObject} />
        </Box>
    );
};

export default DomainComponent;